import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Modal from 'components/Modal';
import styled, { css } from 'styled-components';
import appointmentSelectors from 'features/appointment/services/selectors';
import { Col, Row } from 'antd';
import { formatCurrency } from 'utils/formatCurrency';
import IcCheckService from './IcCheckService';
import { IServiceItemData } from 'services/shop/types/categories';

export type ChangeServiceModalRef = {
  open: (
    callback?: (
      serviceId?: string,
      serviceObject?: IServiceItemData,
      serviceIds?: string[],
      serviceObjects?: IServiceItemData[],
      staffId?: string
    ) => void,
    selectedServiceId?: string,
    staffId?: string
  ) => void;
};

const ChangeServiceModal = forwardRef<
  ChangeServiceModalRef,
  { modalTitle?: string; isMultiple?: boolean }
>((props, ref) => {
  const [visible, setVisible] = useState(false);
  const callbackRef = useRef<
    | ((serviceId?: string, serviceObject?: IServiceItemData, serviceIds?: string[], serviceObjects?: IServiceItemData[], staffId?: string) => void)
    | undefined
  >(undefined);
  const categoriesBase = appointmentSelectors.getCategories();
  const listServiceAll = useMemo(() => {
    return categoriesBase.flatMap((category) => category.services);
  }, [categoriesBase]);
  const [selectedServiceId, setSelectedServiceId] = useState<string[]>([]);
  const [categorySelected, setCategorySelected] = useState('');
  const staffIdRef = useRef<string>();
  
  const handleClickService = (serviceId: string) => {
    if (props?.isMultiple) {
      setSelectedServiceId([...selectedServiceId, serviceId]);
    } else {
      setSelectedServiceId([serviceId]);
    }
  };

  const servicesFilterByCategory = useMemo(() => {
    return categoriesBase.find((category) => category.id === categorySelected)?.services;
  }, [categoriesBase, categorySelected]);

  const handleClose = () => {
    setVisible(false);
  };
  const onSubmit = () => {
    setVisible(false);
    if (props?.isMultiple) {
      const serviceObjects = listServiceAll?.filter((service) =>
        selectedServiceId.includes(service.id)
      );
      callbackRef.current?.(undefined, undefined, selectedServiceId, serviceObjects, staffIdRef.current);
    } else {
      const serviceObject = servicesFilterByCategory?.filter((service) =>
        selectedServiceId.includes(service.id)
      );
      callbackRef.current?.(selectedServiceId[0], serviceObject?.[0], undefined, undefined, staffIdRef.current);
    }
  };

  useImperativeHandle(ref, () => ({
    open: (callback?: () => void, selectedServiceId?: string, staffId?: string) => {
      staffIdRef.current = staffId;
      if (!selectedServiceId) {
        setCategorySelected(categoriesBase?.[0]?.id || '');
        setSelectedServiceId([]);
      } else {
        const categorySelected = categoriesBase.find((category) =>
          category.services.some((service) => service.id === selectedServiceId)
        );
        setCategorySelected(categorySelected?.id || categoriesBase?.[0]?.id || '');
        setSelectedServiceId(selectedServiceId ? [selectedServiceId] : []);
      }
      callbackRef.current = callback;
      setVisible(true);
    },
  }));

  return (
    <Modal
      modalTitle={props.modalTitle || 'Change services'}
      visible={visible}
      onClose={handleClose}
      onSubmit={onSubmit}
      width={'1600px'}
      noneBodyStyle={true}
      noneFooterStyle={true}
      footerPadding={0.5}
      okTitle='OK'
      v2={true}
    >
      <Container>
        <CateGoryContainer>
          {categoriesBase.map((category) => (
            <CategoryItem
              key={category.id}
              selected={categorySelected === category.id}
              onClick={() => setCategorySelected(category.id)}
            >
              <div className='text'>{category.categoryName}</div>
            </CategoryItem>
          ))}
        </CateGoryContainer>
        <ServicesContainer>
          <Row gutter={[16, 8]}>
            {servicesFilterByCategory?.map((service) => (
              <Col xs={24} sm={24} md={24} lg={8} xl={8} key={service.id}>
                <ServiceItem
                  selected={selectedServiceId.includes(service.id)}
                  onClick={() => handleClickService(service.id)}
                >
                  {selectedServiceId.includes(service.id) && (
                    <div className='icon'>
                      <IcCheckService />
                    </div>
                  )}
                  <div className='name'>{service.serviceName}</div>
                  <div className='price'>{formatCurrency(service.priceSell)}</div>
                </ServiceItem>
              </Col>
            ))}
          </Row>
        </ServicesContainer>
      </Container>
    </Modal>
  );
});
ChangeServiceModal.displayName = 'ChangeServiceModal';

export default ChangeServiceModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CateGoryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
  // gap: 10px;
  padding: 1rem;
`;

const CategoryItem = styled.div<{ selected: boolean }>`
  display: flex;
  padding: 25px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  color: #505050;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 30px;
  text-transform: capitalize;
  background: #fcfcfd;
  box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
    0 -3px 0 0 #d6d6e7 inset;
  cursor: pointer;

  ${({ selected }) =>
    selected &&
    css`
      color: #fff;
      background: #2d6772;
      box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
        0 -3px 0 0 rgba(8, 56, 65, 0.5) inset;
    `}
  .text {
    font-size: 18px;
    font-weight: 600;
    line-height: 30px;
    text-transform: capitalize;
    ${({ selected }) =>
      selected &&
      css`
        color: #fff;
      `}
  }
`;

const ServicesContainer = styled.div`
  height: 700px;
  overflow-y: auto;
  padding: 0 1rem;
`;

const ServiceItem = styled.div<{ selected: boolean }>`
  cursor: pointer;
  height: 56px;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-radius: 5px;
  background: #e5e6eb;
  box-sizing: border-box;
  border: 2px solid transparent;
  box-shadow: 0 -4px 4px 0 rgba(0, 0, 0, 0.25) inset, 0 8px 4px 3px rgba(255, 255, 255, 0.25) inset;
  transition: all 0.3s ease;
  ${({ selected }) =>
    selected &&
    css`
      transition: all 0.3s ease;
      border-color: #d2464f;
    `}

  .name {
    flex: 1;
    overflow: hidden;
    color: #1d2129;
    text-overflow: ellipsis;
    font-size: 18px;
    font-weight: 400;
    line-height: normal;
  }
  .price {
    color: #1d2129;
    font-size: 18px;
    font-weight: 600;
    text-transform: capitalize;
  }
  .icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 100px;
    background: #d2464f;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
